import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import {
  useAnalysisJobs,
  type AnalysisJob,
  type AnalysisJobKind,
} from '../context/AnalysisJobsContext';
import { useOpportunityDiscovery } from '../context/OpportunityDiscoveryContext';
import {
  useOpportunityHardening,
  type HardeningJob,
} from '../context/OpportunityHardeningContext';
import {
  useOpportunityPnlGeneration,
  type PnlGenerationJob,
} from '../context/OpportunityPnlGenerationContext';
import {
  useOpportunityRoadmapGeneration,
  type RoadmapGenerationJob,
} from '../context/OpportunityRoadmapGenerationContext';
import { useTheme } from '../context/ThemeProvider';
import styles from './BackgroundTaskToast.module.css';

const DISCOVERY_LOADING_MESSAGES = [
  'Scanning your indexed portfolio data…',
  'Researching adjacent market spaces…',
  'Stress-testing viability against live signals…',
  'Feel free to browse — this can take a few minutes.',
];

const HARDENING_LOADING_MESSAGES = [
  'Baking the fix into the idea…',
  'Rewriting the idea summary…',
  'Re-analyzing the evolved idea…',
  'Feel free to browse — this can take a few minutes.',
];

const ROADMAP_LOADING_MESSAGES = [
  'Mapping the shippable wedge for the first weeks…',
  'Weighing impact against effort per feature…',
  'Sequencing now, next, and later phases…',
  'Feel free to browse — this can take a few minutes.',
];

const PNL_LOADING_MESSAGES = [
  'Building month-by-month revenue and cost lines…',
  'Stress-testing break-even and cash burn…',
  'Modeling years 1–3 toward profitability…',
  'Feel free to browse — this can take a few minutes.',
];

const FOUNDER_UPDATE_LOADING_MESSAGES = [
  'Reading the founder update…',
  'Extracting revenue, burn, and cash metrics…',
  'Scoring sentiment and company health…',
  'Usually takes 30–90 seconds.',
];

const DECK_LOADING_MESSAGES = [
  'Reading the pitch deck…',
  'Scoring team, market, and traction…',
  'Weighing risks against the upside…',
  'Usually takes 30–90 seconds.',
];

const IDEA_LOADING_MESSAGES = [
  'Reading the idea summary…',
  'Scoring market, moat, and execution risk…',
  'Surfacing weak points to harden…',
  'Feel free to browse — this can take a few minutes.',
];

const ANALYSIS_TOAST_COPY: Record<
  AnalysisJobKind,
  { loadingTitle: string; errorTitle: string; messages: string[] }
> = {
  founderUpdate: {
    loadingTitle: 'Analyzing update',
    errorTitle: 'Update analysis stopped',
    messages: FOUNDER_UPDATE_LOADING_MESSAGES,
  },
  deck: {
    loadingTitle: 'Analyzing deck',
    errorTitle: 'Deck analysis stopped',
    messages: DECK_LOADING_MESSAGES,
  },
  idea: {
    loadingTitle: 'Analyzing idea',
    errorTitle: 'Idea analysis stopped',
    messages: IDEA_LOADING_MESSAGES,
  },
};

function useRotatingMessage(active: boolean, messages: string[]) {
  const [messageIndex, setMessageIndex] = useState(0);
  const [messageVisible, setMessageVisible] = useState(true);

  useEffect(() => {
    if (!active) {
      setMessageIndex(0);
      setMessageVisible(true);
      return undefined;
    }

    if (messages.length <= 1) {
      return undefined;
    }

    const interval = window.setInterval(() => {
      setMessageVisible(false);
      window.setTimeout(() => {
        setMessageIndex((current) => (current + 1) % messages.length);
        setMessageVisible(true);
      }, 260);
    }, 3200);

    return () => window.clearInterval(interval);
  }, [active, messages]);

  const message = useMemo(
    () => messages[messageIndex] ?? messages[0] ?? '',
    [messageIndex, messages]
  );

  return { message, messageVisible };
}

function IntelligenceIcon({ isDarkMode }: { isDarkMode: boolean }) {
  return (
    <div className={styles.iconWrap} aria-hidden="true">
      <span className={styles.ring} />
      <img
        className={styles.icon}
        src={
          isDarkMode
            ? '/assets/icons/apple.intelligence.svg'
            : '/assets/icons/apple.intelligence.green.svg'
        }
        alt=""
        width={18}
        height={18}
      />
    </div>
  );
}

function DiscoveryToastCard({
  discovering,
  error,
  onDismissError,
  isDarkMode,
}: {
  discovering: boolean;
  error: string | null;
  onDismissError: () => void;
  isDarkMode: boolean;
}) {
  const { message, messageVisible } = useRotatingMessage(discovering, DISCOVERY_LOADING_MESSAGES);

  if (discovering) {
    return (
      <div className={styles.card} role="status" aria-live="polite" aria-busy="true">
        <div className={styles.inner}>
          <IntelligenceIcon isDarkMode={isDarkMode} />
          <div className={styles.body}>
            <p className={styles.title}>Discovering idea</p>
            <p
              className={`${styles.message} ${
                messageVisible ? styles.messageVisible : styles.messageHidden
              }`}
            >
              {message}
            </p>
            <p className={styles.hint}>You can keep navigating while this runs.</p>
          </div>
        </div>
        <div className={styles.progress} aria-hidden="true">
          <span className={styles.progressBar} />
        </div>
      </div>
    );
  }

  if (!error) {
    return null;
  }

  return (
    <div className={`${styles.card} ${styles.cardError}`} role="alert" aria-live="assertive">
      <div className={styles.inner}>
        <div className={styles.errorIcon} aria-hidden="true">
          !
        </div>
        <div className={styles.body}>
          <div className={styles.headerRow}>
            <p className={styles.title}>Discovery stopped</p>
            <button type="button" className={styles.dismiss} onClick={onDismissError} aria-label="Dismiss">
              ×
            </button>
          </div>
          <p className={styles.message}>{error}</p>
        </div>
      </div>
    </div>
  );
}

function PnlGenerationToastCard({
  job,
  onDismissError,
  isDarkMode,
}: {
  job: PnlGenerationJob;
  onDismissError: (jobId: string) => void;
  isDarkMode: boolean;
}) {
  const loading = job.status === 'loading';
  const { message, messageVisible } = useRotatingMessage(loading, PNL_LOADING_MESSAGES);

  if (loading) {
    return (
      <div className={styles.card} role="status" aria-live="polite" aria-busy="true">
        <div className={styles.inner}>
          <IntelligenceIcon isDarkMode={isDarkMode} />
          <div className={styles.body}>
            <p className={styles.title}>Generating P&L</p>
            <p className={styles.subtitle}>{job.opportunityName}</p>
            <p
              className={`${styles.message} ${
                messageVisible ? styles.messageVisible : styles.messageHidden
              }`}
            >
              {message}
            </p>
            <p className={styles.hint}>
              You can keep navigating —{' '}
              <Link to={`/opportunities/${job.opportunityId}?tab=pnl`}>view opportunity</Link>.
            </p>
          </div>
        </div>
        <div className={styles.progress} aria-hidden="true">
          <span className={styles.progressBar} />
        </div>
      </div>
    );
  }

  if (!job.error) {
    return null;
  }

  return (
    <div className={`${styles.card} ${styles.cardError}`} role="alert" aria-live="assertive">
      <div className={styles.inner}>
        <div className={styles.errorIcon} aria-hidden="true">
          !
        </div>
        <div className={styles.body}>
          <div className={styles.headerRow}>
            <p className={styles.title}>P&L generation stopped</p>
            <button
              type="button"
              className={styles.dismiss}
              onClick={() => onDismissError(job.id)}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
          <p className={styles.subtitle}>{job.opportunityName}</p>
          <p className={styles.message}>{job.error}</p>
        </div>
      </div>
    </div>
  );
}

function RoadmapGenerationToastCard({
  job,
  onDismissError,
  isDarkMode,
}: {
  job: RoadmapGenerationJob;
  onDismissError: (jobId: string) => void;
  isDarkMode: boolean;
}) {
  const loading = job.status === 'loading';
  const { message, messageVisible } = useRotatingMessage(loading, ROADMAP_LOADING_MESSAGES);

  if (loading) {
    return (
      <div className={styles.card} role="status" aria-live="polite" aria-busy="true">
        <div className={styles.inner}>
          <IntelligenceIcon isDarkMode={isDarkMode} />
          <div className={styles.body}>
            <p className={styles.title}>Generating roadmap</p>
            <p className={styles.subtitle}>{job.opportunityName}</p>
            <p
              className={`${styles.message} ${
                messageVisible ? styles.messageVisible : styles.messageHidden
              }`}
            >
              {message}
            </p>
            <p className={styles.hint}>
              You can keep navigating —{' '}
              <Link to={`/opportunities/${job.opportunityId}?tab=roadmap`}>view opportunity</Link>.
            </p>
          </div>
        </div>
        <div className={styles.progress} aria-hidden="true">
          <span className={styles.progressBar} />
        </div>
      </div>
    );
  }

  if (!job.error) {
    return null;
  }

  return (
    <div className={`${styles.card} ${styles.cardError}`} role="alert" aria-live="assertive">
      <div className={styles.inner}>
        <div className={styles.errorIcon} aria-hidden="true">
          !
        </div>
        <div className={styles.body}>
          <div className={styles.headerRow}>
            <p className={styles.title}>Roadmap generation stopped</p>
            <button
              type="button"
              className={styles.dismiss}
              onClick={() => onDismissError(job.id)}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
          <p className={styles.subtitle}>{job.opportunityName}</p>
          <p className={styles.message}>{job.error}</p>
        </div>
      </div>
    </div>
  );
}

function HardeningToastCard({
  job,
  onDismissError,
  isDarkMode,
}: {
  job: HardeningJob;
  onDismissError: (jobId: string) => void;
  isDarkMode: boolean;
}) {
  const loading = job.status === 'loading';
  const { message, messageVisible } = useRotatingMessage(loading, HARDENING_LOADING_MESSAGES);

  if (loading) {
    return (
      <div className={styles.card} role="status" aria-live="polite" aria-busy="true">
        <div className={styles.inner}>
          <IntelligenceIcon isDarkMode={isDarkMode} />
          <div className={styles.body}>
            <p className={styles.title}>Applying fix</p>
            <p className={styles.subtitle}>
              {job.opportunityName} · {job.weakPointTitle}
            </p>
            <p
              className={`${styles.message} ${
                messageVisible ? styles.messageVisible : styles.messageHidden
              }`}
            >
              {message}
            </p>
            <p className={styles.hint}>
              You can keep navigating —{' '}
              <Link to={`/opportunities/${job.opportunityId}?tab=refinery`}>view opportunity</Link>.
            </p>
          </div>
        </div>
        <div className={styles.progress} aria-hidden="true">
          <span className={styles.progressBar} />
        </div>
      </div>
    );
  }

  if (!job.error) {
    return null;
  }

  return (
    <div className={`${styles.card} ${styles.cardError}`} role="alert" aria-live="assertive">
      <div className={styles.inner}>
        <div className={styles.errorIcon} aria-hidden="true">
          !
        </div>
        <div className={styles.body}>
          <div className={styles.headerRow}>
            <p className={styles.title}>Fix stopped</p>
            <button
              type="button"
              className={styles.dismiss}
              onClick={() => onDismissError(job.id)}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
          <p className={styles.subtitle}>{job.opportunityName}</p>
          <p className={styles.message}>{job.error}</p>
        </div>
      </div>
    </div>
  );
}

function AnalysisToastCard({
  job,
  onDismissError,
  isDarkMode,
}: {
  job: AnalysisJob;
  onDismissError: (jobId: string) => void;
  isDarkMode: boolean;
}) {
  const copy = ANALYSIS_TOAST_COPY[job.kind];
  const loading = job.status === 'loading';
  const { message, messageVisible } = useRotatingMessage(loading, copy.messages);

  if (loading) {
    return (
      <div className={styles.card} role="status" aria-live="polite" aria-busy="true">
        <div className={styles.inner}>
          <IntelligenceIcon isDarkMode={isDarkMode} />
          <div className={styles.body}>
            <p className={styles.title}>{copy.loadingTitle}</p>
            <p className={styles.subtitle}>{job.targetLabel}</p>
            <p
              className={`${styles.message} ${
                messageVisible ? styles.messageVisible : styles.messageHidden
              }`}
            >
              {message}
            </p>
            <p className={styles.hint}>
              You can keep navigating — <Link to={job.link}>view details</Link>.
            </p>
          </div>
        </div>
        <div className={styles.progress} aria-hidden="true">
          <span className={styles.progressBar} />
        </div>
      </div>
    );
  }

  if (!job.error) {
    return null;
  }

  return (
    <div className={`${styles.card} ${styles.cardError}`} role="alert" aria-live="assertive">
      <div className={styles.inner}>
        <div className={styles.errorIcon} aria-hidden="true">
          !
        </div>
        <div className={styles.body}>
          <div className={styles.headerRow}>
            <p className={styles.title}>{copy.errorTitle}</p>
            <button
              type="button"
              className={styles.dismiss}
              onClick={() => onDismissError(job.id)}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
          <p className={styles.subtitle}>{job.targetLabel}</p>
          <p className={styles.message}>{job.error}</p>
        </div>
      </div>
    </div>
  );
}

export function BackgroundTaskToastStack() {
  const { discovering, error, dismissError } = useOpportunityDiscovery();
  const { jobs, dismissError: dismissPnlError } = useOpportunityPnlGeneration();
  const { jobs: roadmapJobs, dismissError: dismissRoadmapError } =
    useOpportunityRoadmapGeneration();
  const { jobs: hardeningJobs, dismissError: dismissHardeningError } = useOpportunityHardening();
  const { jobs: analysisJobs, dismissError: dismissAnalysisError } = useAnalysisJobs();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  const visiblePnlJobs = jobs.filter((job) => job.status === 'loading' || Boolean(job.error));
  const visibleRoadmapJobs = roadmapJobs.filter(
    (job) => job.status === 'loading' || Boolean(job.error)
  );
  const visibleHardeningJobs = hardeningJobs.filter(
    (job) => job.status === 'loading' || Boolean(job.error)
  );
  const visibleAnalysisJobs = analysisJobs.filter(
    (job) => job.status === 'loading' || Boolean(job.error)
  );
  const showDiscovery = discovering || Boolean(error);
  const visible =
    showDiscovery ||
    visiblePnlJobs.length > 0 ||
    visibleRoadmapJobs.length > 0 ||
    visibleHardeningJobs.length > 0 ||
    visibleAnalysisJobs.length > 0;

  if (!visible || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div className={styles.stack}>
      {visibleAnalysisJobs.map((job) => (
        <AnalysisToastCard
          key={job.id}
          job={job}
          onDismissError={dismissAnalysisError}
          isDarkMode={isDarkMode}
        />
      ))}
      {visibleHardeningJobs.map((job) => (
        <HardeningToastCard
          key={job.id}
          job={job}
          onDismissError={dismissHardeningError}
          isDarkMode={isDarkMode}
        />
      ))}
      {visiblePnlJobs.map((job) => (
        <PnlGenerationToastCard
          key={job.id}
          job={job}
          onDismissError={dismissPnlError}
          isDarkMode={isDarkMode}
        />
      ))}
      {visibleRoadmapJobs.map((job) => (
        <RoadmapGenerationToastCard
          key={job.id}
          job={job}
          onDismissError={dismissRoadmapError}
          isDarkMode={isDarkMode}
        />
      ))}
      {showDiscovery ? (
        <DiscoveryToastCard
          discovering={discovering}
          error={error}
          onDismissError={dismissError}
          isDarkMode={isDarkMode}
        />
      ) : null}
    </div>,
    document.body
  );
}
